import React, { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Brush,
  ReferenceLine,
} from 'recharts';
import { useApi } from '../apiHelpers';
import DateAvailabilityModal from './DateAvailabilityModal';
import Panel from '../components/ui/Panel';
import Switch from '../components/ui/Switch';
import Alert from '../components/ui/Alert';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Appear from '../components/ui/Appear';
import { CATEGORY, alpha } from '../components/ui/categoryColors';

/* ------------------------------------------------------------------ */
/*  Color thresholds — now based on availability ratio (inverted)      */
/* ------------------------------------------------------------------ */
// Red is overbooked, green is healthy. Drones draw in the mark, batteries in the lighter ink.
const AVAIL_HUES = [
  { upTo: 0, hue: 'red' },
  { upTo: 0.10, hue: 'orange' },
  { upTo: 0.25, hue: 'yellow' },
  { upTo: Infinity, hue: 'green' },
].map(t => ({ upTo: t.upTo, mark: CATEGORY[t.hue].mark.hex, ink: CATEGORY[t.hue].ink.hex }));

const getAvailColor = (available, total) => {
  if (total <= 0) return 'var(--on-surface-variant)';
  const ratio = available / total;
  return AVAIL_HUES.find(t => ratio <= t.upTo).mark;
};

const getBatteryAvailColor = (available, total) => {
  if (total <= 0) return 'var(--on-surface-variant)';
  const ratio = available / total;
  return AVAIL_HUES.find(t => ratio <= t.upTo).ink;
};

// The hero figures' ink: the bars' four colours, so a figure reads like the line it summarises.
const AVAIL_TIERS = [
  { upTo: 0, hue: 'red' },
  { upTo: 0.10, hue: 'orange' },
  { upTo: 0.25, hue: 'yellow' },
];

const getAvailInk = (available, total) => {
  if (total <= 0) return 'var(--on-surface-variant)';
  const ratio = available / total;
  const tier = AVAIL_TIERS.find(t => ratio <= t.upTo);
  return CATEGORY[tier ? tier.hue : 'green'].ink.oklch;
};

// Pyro stays purple until it is low, then borrows the warning colours.
const getPyroInk = (available, total) => {
  if (total <= 0 || available / total > 0.25) return CATEGORY.purple.ink.oklch;
  return CATEGORY[available <= 0 ? 'red' : 'yellow'].ink.oklch;
};

// One hero figure: the number in its colour, its name under it, its context under that.
const HeroFigure = ({ ink, value, label, sub }) => (
  <div className="min-w-0 text-center">
    <div className="text-2xl font-bold tabular-nums" style={{ color: ink }}>{value}</div>
    <div className="mt-0.5 text-[11px] text-on-surface-variant">{label}</div>
    <div className="text-[10px] text-on-surface-variant tabular-nums">{sub}</div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Custom tooltip                                                     */
/* ------------------------------------------------------------------ */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const d = payload[0]?.payload;
  if (!d) return null;

  const events = d.events || [];
  const rentals = d.rentals || [];
  const leases = d.leases || [];
  const showDayEvents = events.filter(e => e.isShowDay);
  const transitEvents = events.filter(e => !e.isShowDay);

  if (showDayEvents.length === 0 && transitEvents.length === 0 && rentals.length === 0 && leases.length === 0) return null;

  return (
    <div className="bg-gray-900 border border-gray-700/80 rounded-md px-3.5 py-2.5 shadow-overlay text-sm"
         style={{ maxWidth: 280, maxHeight: 380, overflowY: 'auto', zIndex: 50 }}>
      {/* Lease summary at top — single total row */}
      {leases.length > 0 && (() => {
        const totalLs = leases.reduce((s, l) => s + (l.ls || 0), 0);
        const totalPyro = leases.reduce((s, l) => s + (l.pyro || 0), 0);
        return (
          <div className="mb-2 pb-2 border-b border-gray-700/50">
            <div className="flex justify-between items-center gap-4 text-xs">
              <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: alpha(CATEGORY.blue.ink, 0.8) }}>
                Leased Out <span className="text-on-surface-variant normal-case tracking-normal">({leases.length})</span>
              </span>
              <span className="tabular-nums flex-shrink-0" style={{ color: CATEGORY.blue.ink.oklch }}>
                {totalLs > 0 && totalLs}
                {totalPyro > 0 && <span className="ml-1" style={{ color: CATEGORY.purple.ink.oklch }}>+{totalPyro}P</span>}
              </span>
            </div>
          </div>
        );
      })()}

      {showDayEvents.length > 0 && (
        <div>
          <div className="text-[10px] text-primary uppercase tracking-wider font-medium mb-1">Show Day</div>
          {showDayEvents.map((evt, i) => (
            <div key={`s${i}`} className="flex justify-between gap-4 text-xs py-0.5">
              <span className="text-on-surface truncate">{evt.isTour ? '\uD83D\uDD17 ' : ''}{evt.name}</span>
              <span className="text-on-surface-variant tabular-nums flex-shrink-0">
                {evt.drones}{evt.pyro > 0 && <span className="ml-1" style={{ color: CATEGORY.purple.ink.oklch }}>({evt.pyro}P)</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {transitEvents.length > 0 && (
        <div className={showDayEvents.length > 0 ? 'mt-2 pt-2 border-t border-gray-700/50' : ''}>
          <div className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium mb-1">Transit</div>
          {transitEvents.map((evt, i) => (
            <div key={`t${i}`} className="flex justify-between gap-4 text-xs py-0.5">
              <span className="text-on-surface-variant truncate">{evt.isTour ? '\uD83D\uDD17 ' : ''}{evt.name}</span>
              <span className="text-on-surface-variant tabular-nums flex-shrink-0">
                {evt.drones}{evt.pyro > 0 && <span className="ml-1" style={{ color: alpha(CATEGORY.purple.ink, 0.6) }}>({evt.pyro}P)</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {rentals.length > 0 && (
        <div className={(showDayEvents.length > 0 || transitEvents.length > 0) ? 'mt-2 pt-2 border-t border-gray-700/50' : ''}>
          <div className="text-[10px] uppercase tracking-wider font-medium mb-1" style={{ color: alpha(CATEGORY.orange.ink, 0.8) }}>Rentals</div>
          {rentals.map((r, i) => (
            <div key={`r${i}`} className="flex justify-between gap-4 text-xs py-0.5">
              <span className="truncate" style={{ color: alpha(CATEGORY.orange.ink, 0.8) }}>{r.name}</span>
              <span className="tabular-nums flex-shrink-0" style={{ color: alpha(CATEGORY.orange.ink, 0.6) }}>
                {r.ls}{r.pyro > 0 && <span className="ml-1">+{r.pyro}P</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-on-surface-variant mt-2 text-center">Click for details</div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Snap to next 500                                                   */
/* ------------------------------------------------------------------ */
const snapTo500 = (val) => Math.ceil(val / 500) * 500;

/* ------------------------------------------------------------------ */
/*  Forecast chart — shows AVAILABLE (total - committed)               */
/* ------------------------------------------------------------------ */
const ForecastChart = () => {
  const api = useApi();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [brushStart, setBrushStart] = useState(0);
  const [brushEnd, setBrushEnd] = useState(null);
  const [modalDate, setModalDate] = useState(null);
  const [includePending, setIncludePending] = useState(false);
  const showLeases = true;  // Leases always shown in availability view
  const [hoveredData, setHoveredData] = useState(null);

  useEffect(() => {
    const fetchForecast = async () => {
      try {
        const params = includePending ? '?include_pending=true' : '';
        const result = await api.get(`/dashboard/forecast${params}`);
        setForecast(result);
      } catch (err) {
        console.error('Failed to load forecast:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchForecast();
  }, [includePending]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Transform data: API now returns availableDrones (with leases pre-subtracted)
  const chartData = useMemo(() => {
    if (!forecast?.series?.length) return [];
    return forecast.series.map(d => ({
      ...d,
      // availableDrones already = availableFleet - committedDrones (from API)
      availableBatteries: d.totalBatteries - d.committedBatteries,
      availablePyro: (forecast.totalPyro || 0) - (d.committedPyro || 0),
      // For "Show Leases" mode: the top line becomes totalFleet (Internal Fleet)
      // and we show a lease layer between totalFleet and availableFleet
      internalFleetLine: showLeases ? d.totalFleet : null,
    }));
  }, [forecast, showLeases]);

  // Gradient for available drones line — green when healthy, red when low/negative
  const availGradientStops = useMemo(() => {
    if (!chartData.length) return [];
    const start = brushStart;
    const end = brushEnd != null ? brushEnd : chartData.length - 1;
    const visibleLen = end - start + 1;
    if (visibleLen <= 0) return [];

    const stops = [];
    let prevColor = null;

    for (let i = 0; i < visibleLen; i++) {
      const d = chartData[start + i];
      const color = getAvailColor(d.availableDrones, d.totalFleet);
      const offset = visibleLen > 1 ? i / (visibleLen - 1) : 0;

      if (color !== prevColor) {
        if (prevColor !== null) {
          stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color: prevColor });
        }
        stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color });
        prevColor = color;
      }
    }
    return stops;
  }, [chartData, brushStart, brushEnd]);

  const availFillStops = useMemo(() => {
    return availGradientStops.map(s => ({ ...s, opacity: 0.15 }));
  }, [availGradientStops]);

  // Battery available gradient
  const battAvailGradientStops = useMemo(() => {
    if (!chartData.length) return [];
    const start = brushStart;
    const end = brushEnd != null ? brushEnd : chartData.length - 1;
    const visibleLen = end - start + 1;
    if (visibleLen <= 0) return [];

    const stops = [];
    let prevColor = null;

    for (let i = 0; i < visibleLen; i++) {
      const d = chartData[start + i];
      const color = getBatteryAvailColor(d.availableBatteries, d.totalBatteries);
      const offset = visibleLen > 1 ? i / (visibleLen - 1) : 0;

      if (color !== prevColor) {
        if (prevColor !== null) {
          stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color: prevColor });
        }
        stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color });
        prevColor = color;
      }
    }
    return stops;
  }, [chartData, brushStart, brushEnd]);

  // Pyro available gradient — purple color-shifting
  const pyroGradientStops = useMemo(() => {
    if (!chartData.length || !forecast?.totalPyro) return [];
    const start = brushStart;
    const end = brushEnd != null ? brushEnd : chartData.length - 1;
    const visibleLen = end - start + 1;
    if (visibleLen <= 0) return [];

    const stops = [];
    let prevColor = null;

    for (let i = 0; i < visibleLen; i++) {
      const d = chartData[start + i];
      const total = forecast.totalPyro;
      const ratio = total > 0 ? d.availablePyro / total : 1;
      const color = ratio <= 0.25 ? AVAIL_HUES.find(t => ratio <= t.upTo).mark : CATEGORY.purple.mark.hex;
      const offset = visibleLen > 1 ? i / (visibleLen - 1) : 0;

      if (color !== prevColor) {
        if (prevColor !== null) {
          stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color: prevColor });
        }
        stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color });
        prevColor = color;
      }
    }
    return stops;
  }, [chartData, brushStart, brushEnd, forecast?.totalPyro]);

  const pyroFillStops = useMemo(() => {
    return pyroGradientStops.map(s => ({ ...s, opacity: 0.1 }));
  }, [pyroGradientStops]);

  const todayStr = new Date().toISOString().split('T')[0];

  // Data for hero stats: hovered date or today
  const todayData = useMemo(() => chartData.find(d => d.date === todayStr) || chartData[0], [chartData, todayStr]);
  const activeData = hoveredData || todayData;

  if (loading) {
    return (
      <Panel title="Availability Forecast" titleStyle="section" accent="green" lit>
        <SkeletonGroup label="Loading forecast">
          <Skeleton className="h-64 w-full rounded-panel" />
        </SkeletonGroup>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel title="Availability Forecast" titleStyle="section" accent="green" lit>
        <Alert variant="note" tone="error">Failed to load forecast</Alert>
      </Panel>
    );
  }

  if (!chartData.length) return null;

  // Y-axis: snap to next 100, allow negative
  const minAvail = Math.min(
    ...chartData.map(d => d.availableDrones),
    ...chartData.map(d => d.availableBatteries),
    ...chartData.map(d => d.availablePyro),
  );
  const maxAvail = Math.max(
    forecast.totalFleet,
    forecast.totalBatteries || 0,
    ...chartData.map(d => d.availableDrones),
    ...chartData.map(d => d.availableBatteries),
  );

  const yMin = minAvail < 0 ? -snapTo500(Math.abs(minAvail)) : 0;
  const yMax = snapTo500(maxAvail);

  return (
    <Panel
      title="Availability Forecast"
      titleStyle="section"
      accent="green"
      lit
      actions={(
        <Switch
          label="Include pending"
          checked={includePending}
          onChange={() => setIncludePending(p => !p)}
        />
      )}
    >
      {/* Hero stats — updates on hover, falls back to today */}
      {activeData && (() => {
        const hasPyro = forecast.totalPyro > 0;
        return (
          <div className="mb-4 text-center">
            <div className="mb-2 text-xs text-on-surface-variant">
              {hoveredData ? new Date(activeData.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Today'}
            </div>
            <div className={`grid ${hasPyro ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <HeroFigure
                ink={getAvailInk(activeData.availableDrones, activeData.totalFleet)}
                value={activeData.availableDrones.toLocaleString()}
                label="Available Drones"
                sub={(
                  <>
                    {showLeases && activeData.totalLeased > 0
                      ? `of ${activeData.availableFleet?.toLocaleString()} avail, ${activeData.committedDrones.toLocaleString()} committed, `
                      : `of ${activeData.totalFleet.toLocaleString()}, ${activeData.committedDrones.toLocaleString()} committed`}
                    {showLeases && activeData.totalLeased > 0 && (
                      <span style={{ color: CATEGORY.blue.ink.oklch }}>{activeData.totalLeased.toLocaleString()} leased</span>
                    )}
                  </>
                )}
              />
              <HeroFigure
                ink={getAvailInk(activeData.availableBatteries, activeData.totalBatteries)}
                value={activeData.availableBatteries.toLocaleString()}
                label="Available Batteries"
                sub={`of ${activeData.totalBatteries.toLocaleString()}, ${activeData.committedBatteries.toLocaleString()} committed`}
              />
              {hasPyro && (
                <HeroFigure
                  ink={getPyroInk(activeData.availablePyro, forecast.totalPyro)}
                  value={activeData.availablePyro.toLocaleString()}
                  label="Available Pyro"
                  sub={`of ${forecast.totalPyro.toLocaleString()}, ${(activeData.committedPyro || 0).toLocaleString()} committed`}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}
          onClick={(e) => { if (e?.activePayload?.[0]?.payload?.date) setModalDate(e.activePayload[0].payload.date); }}
          onMouseMove={(e) => { if (e?.activePayload?.[0]?.payload) setHoveredData(e.activePayload[0].payload); }}
          onMouseLeave={() => setHoveredData(null)}
          style={{ cursor: 'pointer' }}>
          <defs>
            <linearGradient id="gradAvailStroke" x1="0" y1="0" x2="1" y2="0">
              {availGradientStops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={1} />
              ))}
            </linearGradient>
            <linearGradient id="gradAvailFill" x1="0" y1="0" x2="1" y2="0">
              {availFillStops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity} />
              ))}
            </linearGradient>
            <linearGradient id="gradBattAvailStroke" x1="0" y1="0" x2="1" y2="0">
              {battAvailGradientStops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={1} />
              ))}
            </linearGradient>
            <linearGradient id="gradPyroStroke" x1="0" y1="0" x2="1" y2="0">
              {pyroGradientStops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={1} />
              ))}
            </linearGradient>
            <linearGradient id="gradPyroFill" x1="0" y1="0" x2="1" y2="0">
              {pyroFillStops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity} />
              ))}
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(55,65,81,0.5)"
            vertical={false}
          />

          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fill: 'var(--on-surface-variant)', fontSize: 10 }}
            axisLine={{ stroke: '#374151' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={50}
          />

          <YAxis
            domain={[yMin, yMax]}
            allowDataOverflow={true}
            ticks={(() => {
              const t = [];
              for (let v = yMin; v <= yMax; v += 500) t.push(v);
              if (!t.includes(0)) t.push(0);
              return t.sort((a, b) => a - b);
            })()}
            interval={0}
            tick={{ fill: 'var(--on-surface-variant)', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={45}
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: 'rgba(156,163,175,0.2)' }}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{ pointerEvents: 'none', zIndex: 20 }}
          />

          {/* Today reference line — no label */}
          <ReferenceLine
            x={todayStr}
            stroke="rgba(156,163,175,0.4)"
            strokeDasharray="4 4"
          />

          {/* Zero line — always visible, bold */}
          <ReferenceLine
            y={0}
            stroke="rgba(255,255,255,0.25)"
            strokeWidth={2}
          />

          {/* Show Leases mode: Internal Fleet flat line at top */}
          {showLeases && (
            <ReferenceLine
              y={forecast.totalFleet}
              stroke="rgba(255,204,51,0.3)"
              strokeDasharray="6 3"
            />
          )}

          {/* Show Leases mode: Available Fleet line (after lease subtraction) */}
          {showLeases && (
            <Line
              type="monotone"
              dataKey="availableFleet"
              stroke={alpha(CATEGORY.orange.ink, 0.6)}
              strokeWidth={1}
              strokeDasharray="4 4"
              dot={false}
              activeDot={false}
              name="Available for Shows"
            />
          )}

          {/* Available drones — color-shifting area (high = green, low = red) */}
          <Area
            type="monotone"
            dataKey="availableDrones"
            fill="url(#gradAvailFill)"
            stroke="url(#gradAvailStroke)"
            strokeWidth={2}
            name="Available Drones"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2 }}
            baseValue={0}
          />

          {/* Available batteries — dashed line */}
          <Line
            type="monotone"
            dataKey="availableBatteries"
            stroke="url(#gradBattAvailStroke)"
            strokeWidth={1.5}
            strokeDasharray="6 3"
            name="Available Batteries"
            dot={false}
            activeDot={{ r: 3 }}
          />

          {/* Available pyro — purple area (toggleable), dips down when committed */}
          {forecast.totalPyro > 0 && (
            <Area
              type="monotone"
              dataKey="availablePyro"
              fill="url(#gradPyroFill)"
              stroke="url(#gradPyroStroke)"
              strokeWidth={1.5}
              name="Available Pyro"
              dot={false}
              activeDot={{ r: 3, fill: CATEGORY.purple.mark.hex }}
              baseValue={0}
            />
          )}

          {/* Brush for zoom */}
          <Brush
            dataKey="date"
            height={28}
            stroke="#374151"
            fill="#1f2937"
            tickFormatter={formatDate}
            onChange={({ startIndex, endIndex }) => {
              setBrushStart(startIndex);
              setBrushEnd(endIndex);
            }}
          >
            <ComposedChart>
              <Area type="monotone" dataKey="availableDrones" fill={CATEGORY.green.mark.hex} fillOpacity={0.12} stroke={CATEGORY.green.mark.hex} strokeWidth={0.5} />
            </ComposedChart>
          </Brush>
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-3 text-[10px] text-on-surface-variant">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 rounded inline-block" style={{ background: CATEGORY.green.mark.oklch }} />
          Available Drones
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 border-t border-dashed inline-block" style={{ borderColor: CATEGORY.green.ink.oklch }} />
          Available Batteries
        </span>
        <Appear show={forecast.totalPyro > 0} className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 rounded inline-block" style={{ background: CATEGORY.purple.mark.oklch }} />
          Available Pyro
        </Appear>
        <Appear show={showLeases} className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 border-t border-dashed inline-block" style={{ borderColor: alpha(CATEGORY.orange.ink, 0.6) }} />
          Available for Shows
        </Appear>
      </div>
      <div className="text-[10px] text-on-surface-variant mt-1 text-center">
        Drag the handles to zoom &middot; Click a date for details
      </div>

      {modalDate && (
        <DateAvailabilityModal date={modalDate} onClose={() => setModalDate(null)} />
      )}

      <style>{`
        .recharts-brush > rect:first-of-type { fill: #1f2937 !important; }
        .recharts-brush .recharts-brush-slide { fill: rgba(55,65,81,0.4) !important; }
        .recharts-brush .recharts-brush-traveller rect { fill: var(--outline) !important; rx: 2; }
      `}</style>
    </Panel>
  );
};

export default ForecastChart;
